import type React from "react";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Package,
  MapPin,
  Truck,
  Building2,
  Thermometer,
  ShieldAlert,
  AlertTriangle,
  Clock,
  Loader,
  File,
  Eye,
  Download,
  Upload,
  Lock,
  Mail,
  ArrowRight,
  RotateCcw,
  CheckCircle,
  Ship,
  Plane,
} from "lucide-react";
import { STATUS_STEPS } from "../../constants/formOptions";
import { displayCargoNumber } from "../../utils/cargoDisplay";
import { useAppDispatch } from "../../hooks/useStore";
import {
  clearCurrentShipment,
  downloadShipmentAttachment,
  uploadPublicAttachment,
} from "../../store/slices/shipmentSlice";
import { getCountries } from "../../store/slices/companySlice";
import type {
  Country,
  DriverAttachment,
  AttachmentMeta,
  Shipment,
} from "../../types";
import {
  publicShipmentAPI,
  shipmentAPI,
  publicReclamationAPI,
  pohAPI,
} from "../../services/api";
import { QRCodeSVG } from "qrcode.react";
import ReclamationModal from "../../components/shipments/ReclamationModal";
import SignaturePad from "../../components/UI/SignaturePad";
import {
  FileDropzone,
  GeoStatusBanner,
  useGeoCapture,
} from "../../components/poh/PohEvidence";
import POHChainCard, {
  type POHStepData,
} from "../../components/poh/POHChainCard";
import type { PreviousSignature } from "../../components/poh/POHPreviousSignatures";
import type { Reclamation, ReclamationStatus } from "../../types/reclamation";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

type GatePhase = "loading" | "email-input" | "otp-input" | "granted" | "error";
type EmailSubPhase = "idle" | "denied" | "requesting" | "requested";

const SESSION_KEY = (id: string) => `ship_session_${id}`;
const ROLE_KEY = (id: string) => `ship_role_${id}`;
const EMAIL_KEY = (id: string) => `ship_email_${id}`;

function safeDate(
  value: string | number | Date | null | undefined,
): Date | null {
  if (!value) return null;
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  const d = new Date(value as string | number);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(value: string | number | Date | null | undefined) {
  const d = safeDate(value);
  return d ? d.toLocaleDateString("en-US") : "-";
}

const ShipmentPublicView: React.FC = () => {
  // Gate state
  const [userRole, setUserRole] = useState<"view" | "poh_handler">("view");
  const [phase, setPhase] = useState<GatePhase>("loading");
  const [emailSubPhase, setEmailSubPhase] = useState<EmailSubPhase>("idle");
  const [canRequest, setCanRequest] = useState(false);
  const [alreadyRequested, setAlreadyRequested] = useState(false);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [gateEmail, setGateEmail] = useState("");
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Shipment data (local — not Redux for the public view)
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [isStatusHovered, setIsStatusHovered] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reclamationOpen, setReclamationOpen] = useState(false);
  const [myReclamations, setMyReclamations] = useState<Reclamation[]>([]);

  // Delivery handover (POH). The rendered signature data lives in
  // `pohSignatureChain`; the raw POH object isn't read directly.
  const [deliveryDriverSignUrl, setDeliveryDriverSignUrl] = useState<
    string | null
  >(null);
  const [pohSignatureChain, setPohSignatureChain] = useState<
    PreviousSignature[]
  >([]);
  const [pohLoading, setPohLoading] = useState(false);
  const [customerSignerName, setCustomerSignerName] = useState("");
  const [customerSignatureData, setCustomerSignatureData] = useState("");
  const [customerSignFiles, setCustomerSignFiles] = useState<File[]>([]);
  const customerSignGeo = useGeoCapture(false);
  const [submittingCustomerSign, setSubmittingCustomerSign] = useState(false);
  const [loadingReclamations, setLoadingReclamations] = useState(false);

  const dispatch = useAppDispatch();
  const { t } = useTranslation("shipments");

  const [searchParams] = useSearchParams();
  const shipmentId = searchParams.get("shipmentId") ?? "";

  // verified email — read from sessionStorage so it survives page revisits via stored token
  const resolvedEmail =
    sessionStorage.getItem(EMAIL_KEY(shipmentId)) || gateEmail;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Order":
        return "bg-gray-100 text-gray-700";
      case "ReadyForPickup":
        return "bg-purple-100 text-purple-700";
      case "AwaitingPickup":
        return "bg-orange-100 text-orange-800";
      case "InTransit":
        return "bg-blue-100 text-blue-700";
      case "Completed":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  // Fetch shipment with optional session token
  const fetchShipment = useCallback(
    async (sessionToken?: string) => {
      try {
        const res = await publicShipmentAPI.getShipment(
          shipmentId,
          sessionToken,
        );
        setShipment(res.data.data.shipment);
        if (res.data.data.role) {
          setUserRole(res.data.data.role);
          sessionStorage.setItem(ROLE_KEY(shipmentId), res.data.data.role);
        }
        setPhase("granted");
      } catch (err: unknown) {
        const e = err as {
          response?: { status?: number; data?: { gated?: boolean } };
        };
        if (e.response?.status === 403 && e.response?.data?.gated) {
          setPhase("email-input");
        } else {
          setPhase("error");
        }
      }
    },
    [shipmentId],
  );

  // Initialize
  useEffect(() => {
    if (!shipmentId) {
      setPhase("error");
      return;
    }
    const init = async () => {
      const [countriesRes] = await Promise.allSettled([
        dispatch(getCountries()).unwrap(),
      ]);
      if (countriesRes.status === "fulfilled") {
        setCountries(countriesRes.value);
      }

      // Logged-in users bypass the OTP gate entirely
      const authToken = localStorage.getItem("token");
      if (authToken) {
        try {
          const res = await shipmentAPI.getShipment(shipmentId);
          setShipment(res.data.data.shipment);
          setPhase("granted");
        } catch {
          setPhase("error");
        }
        return;
      }

      const storedToken = sessionStorage.getItem(SESSION_KEY(shipmentId));
      const storedRole = sessionStorage.getItem(ROLE_KEY(shipmentId));
      const storedEmail = sessionStorage.getItem(EMAIL_KEY(shipmentId));
      if (storedRole === "poh_handler") setUserRole("poh_handler");
      if (storedEmail) setGateEmail(storedEmail);
      await fetchShipment(storedToken ?? undefined);
    };
    init();
    return () => {
      dispatch(clearCurrentShipment());
    };
  }, [dispatch, shipmentId, fetchShipment]);

  // OTP handlers
  const handleSendOtp = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(gateEmail.trim())) {
      toast.error("Enter a valid email address");
      return;
    }
    setSendingOtp(true);
    try {
      await publicShipmentAPI.requestOtp(shipmentId, gateEmail.trim());
      setOtpValues(["", "", "", "", "", ""]);
      setOtpError("");
      setPhase("otp-input");
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err: unknown) {
      const e = err as {
        response?: {
          status?: number;
          data?: {
            message?: string;
            canRequest?: boolean;
            alreadyRequested?: boolean;
          };
        };
      };
      if (e.response?.status === 403) {
        const data = e.response.data ?? {};
        setCanRequest(data.canRequest ?? false);
        setAlreadyRequested(data.alreadyRequested ?? false);
        setEmailSubPhase("denied");
      } else {
        toast.error(e.response?.data?.message ?? "Failed to send code");
      }
    } finally {
      setSendingOtp(false);
    }
  };

  const handleRequestAccess = async () => {
    setRequestingAccess(true);
    try {
      await publicShipmentAPI.submitAccessRequest(shipmentId, gateEmail.trim());
      setEmailSubPhase("requested");
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(e.response?.data?.message ?? "Failed to submit request");
    } finally {
      setRequestingAccess(false);
    }
  };

  const verifyOtpCode = useCallback(
    async (otp: string) => {
      setVerifyingOtp(true);
      setOtpError("");
      try {
        const res = await publicShipmentAPI.verifyOtp(
          shipmentId,
          gateEmail.trim(),
          otp,
        );
        const { sessionToken, role } = res.data.data;
        sessionStorage.setItem(SESSION_KEY(shipmentId), sessionToken);
        sessionStorage.setItem(
          EMAIL_KEY(shipmentId),
          gateEmail.trim().toLowerCase(),
        );
        if (role) {
          setUserRole(role);
          sessionStorage.setItem(ROLE_KEY(shipmentId), role);
        }
        await fetchShipment(sessionToken);
      } catch (err: unknown) {
        const e = err as { response?: { data?: { message?: string } } };
        const msg = e.response?.data?.message ?? "Invalid code";
        setOtpError(msg);
        setOtpValues(["", "", "", "", "", ""]);
        setTimeout(() => otpRefs.current[0]?.focus(), 50);
      } finally {
        setVerifyingOtp(false);
      }
    },
    [shipmentId, gateEmail, fetchShipment],
  );

  const handleOtpInput = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otpValues];
    next[index] = digit;
    setOtpValues(next);
    if (digit && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
    if (next.every((d) => d !== "")) {
      verifyOtpCode(next.join(""));
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!pasted) return;
    const next = [...otpValues];
    pasted.split("").forEach((d, i) => {
      if (i < 6) next[i] = d;
    });
    setOtpValues(next);
    otpRefs.current[Math.min(pasted.length, 5)]?.focus();
    if (pasted.length === 6) {
      verifyOtpCode(pasted);
    }
  };

  // Derive options from state
  const countryOptions = useMemo(
    () =>
      countries.flat().map((country) => ({
        value: country._id,
        label: country.name,
      })),
    [countries],
  );

  const getCurrentStepIndex = () => {
    if (!shipment) return 0;
    return STATUS_STEPS.findIndex((step) => step.key === shipment.status);
  };

  const formatTime = (time?: string) => {
    if (!time) return null;
    const d = new Date(time);
    return isNaN(d.getTime())
      ? null
      : d.toLocaleDateString("en-GB") +
          "  " +
          d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const downloadAttachment = async (
    attachment: DriverAttachment | AttachmentMeta,
  ) => {
    try {
      const response = await fetch(attachment.fileUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = attachment.originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      // download failed silently
    }
  };

  const handleDownloadShipmentAttachment = async (
    attachmentId: string,
    previewOnly = false,
  ) => {
    if (!shipment?._id || !attachmentId) return;
    try {
      await dispatch(
        downloadShipmentAttachment({
          shipmentId: shipment._id,
          attachmentId,
          previewOnly,
        }),
      ).unwrap();
    } catch {
      // download failed silently
    }
  };

  const openAttachment = (attachment: DriverAttachment | AttachmentMeta) => {
    window.open(attachment.fileUrl, "_blank");
  };

  const getStepStatus = useCallback(
    (stepKey: string): string | null => {
      if (!shipment) return null;
      const da = shipment.driverAccess || {};

      const eta = `ETA: ${formatDate(shipment?.delivery?.date)} ${
        shipment?.delivery?.time || "-"
      }`;

      const shipmentCreationDate = shipment.shipmentDate;

      const isPresent = (v: unknown) =>
        v !== undefined &&
        v !== null &&
        (typeof v === "boolean" ? v === true : String(v).trim() !== "");

      const reached: Record<string, boolean> = {
        Order: true,
        AwaitingPickup:
          isPresent(da.awaitingPickup) ||
          isPresent(da.awaitingPickupTime) ||
          isPresent(da.journeyStarted) ||
          isPresent(da.journeyStartTime) ||
          isPresent(da.journeyCompleted) ||
          isPresent(da.journeyCompletedTime),
        InTransit:
          isPresent(da.journeyStarted) ||
          isPresent(da.journeyStartTime) ||
          isPresent(da.journeyCompleted) ||
          isPresent(da.journeyCompletedTime),
        Completed:
          isPresent(da.journeyCompleted) || isPresent(da.journeyCompletedTime),
      };

      const stepOrder = [
        "Order",
        "ReadyForPickup",
        "AwaitingPickup",
        "InTransit",
        "Completed",
      ];
      const stepIndex = stepOrder.indexOf(stepKey);
      if (stepIndex === -1) return "Pending";

      let furthest = 0;
      for (let i = stepOrder.length - 1; i >= 0; i--) {
        if (reached[stepOrder[i]]) {
          furthest = i;
          break;
        }
      }

      const timeFor = (key: string) => {
        if (key === "Order" && shipmentCreationDate)
          return formatTime(shipmentCreationDate);
        if (key === "AwaitingPickup" && da.awaitingPickupTime)
          return formatTime(da.awaitingPickupTime);
        if (key === "InTransit" && da.journeyStartTime)
          return formatTime(da.journeyStartTime);
        if (key === "Completed" && da.journeyCompletedTime)
          return formatTime(da.journeyCompletedTime);
        return null;
      };

      if (stepKey === "Completed") {
        if (reached.Completed) {
          return timeFor("Completed") ?? "Completed";
        }
        return eta;
      }

      if (stepIndex < furthest) {
        const t = timeFor(stepKey);
        return t ?? "Completed";
      }

      if (stepKey === "ReadyForPickup") {
        return "In Progress";
      }

      if (stepIndex === furthest) {
        const ownEvidence =
          (stepKey === "Order" &&
            (isPresent(shipmentCreationDate) ||
              isPresent(shipmentCreationDate))) ||
          (stepKey === "AwaitingPickup" &&
            (isPresent(da.awaitingPickup) ||
              isPresent(da.awaitingPickupTime))) ||
          (stepKey === "InTransit" &&
            (isPresent(da.journeyStarted) || isPresent(da.journeyStartTime))) ||
          (stepKey === "Completed" &&
            (isPresent(da.journeyCompleted) ||
              isPresent(da.journeyCompletedTime))) ||
          (stepKey === "Order" && furthest > 0);

        if (ownEvidence) {
          const t = timeFor(stepKey);
          return t ?? "Completed";
        }

        return "In Progress";
      }

      return "Pending";
    },
    [shipment],
  );

  // Sum stored per-row ldm / volumeM3. Auto-fill happens in the form (when
  // dimensions are set + field is empty) — by the time we see the shipment
  // here, the row already has its final value (formula-derived or manual).
  const totalLDM = useMemo(
    () =>
      (shipment?.cargoItems ?? []).reduce((sum, item) => {
        const ldm = Number(item.ldm);
        return Number.isFinite(ldm) && ldm > 0 ? sum + ldm : sum;
      }, 0),
    [shipment?.cargoItems],
  );

  const totalVolume = useMemo(
    () =>
      (shipment?.cargoItems ?? []).reduce((sum, item) => {
        const v = Number(item.volumeM3);
        return Number.isFinite(v) && v > 0 ? sum + v : sum;
      }, 0),
    [shipment?.cargoItems],
  );

  // Transport-mode-aware display (legacy shipments without the field are ROAD).
  const transportMode = shipment?.transportMode || "ROAD";
  const isRoad = transportMode === "ROAD";
  const isAir = transportMode === "AIR";
  // Stored loadType values never change; only the label per mode does:
  // Full/Partial (ROAD) -> FCL/LCL (SEA) -> FTL/LTL (AIR).
  const loadTypeLabel = shipment?.loadType
    ? transportMode === "SEA"
      ? shipment.loadType === "Full Load"
        ? "FCL"
        : "LCL"
      : transportMode === "AIR"
        ? shipment.loadType === "Full Load"
          ? "FTL"
          : "LTL"
        : shipment.loadType
    : "";

  const handlePublicUpload = async (files: File[]) => {
    if (!files.length || !shipmentId) return;
    setUploading(true);
    try {
      await dispatch(uploadPublicAttachment({ shipmentId, files })).unwrap();
      toast.success(t("publicView.uploadSuccess"));
      const sessionToken = sessionStorage.getItem(SESSION_KEY(shipmentId));
      const res = await publicShipmentAPI.getShipment(
        shipmentId,
        sessionToken ?? undefined,
      );
      setShipment(res.data.data.shipment);
    } catch {
      toast.error(t("publicView.uploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  // Fetch all reclamations for this shipment as soon as the visitor is verified
  useEffect(() => {
    if (phase !== "granted" || !shipmentId) return;
    setLoadingReclamations(true);
    publicReclamationAPI
      .getReclamations(shipmentId, "customer")
      .then((res) => setMyReclamations(res.data.data.reclamations ?? []))
      .catch(() => {
        /* silent */
      })
      .finally(() => setLoadingReclamations(false));
  }, [phase, shipmentId]);

  // Fetch delivery POH once verified
  useEffect(() => {
    if (phase !== "granted" || !shipmentId) return;
    setPohLoading(true);
    pohAPI
      .getDeliveryPoh(shipmentId)
      .then((res) => {
        setDeliveryDriverSignUrl(res.data.data.deliveryDriverSignUrl ?? null);
        setPohSignatureChain(res.data.data.signatureChain ?? []);
      })
      .catch(() => {
        /* silent */
      })
      .finally(() => setPohLoading(false));
  }, [phase, shipmentId]);

  const handleCustomerDeliverySign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerSignerName.trim())
      return toast.error("Please enter your name");
    if (!customerSignatureData)
      return toast.error("Please provide your signature");
    if (customerSignGeo.state.status !== "ok")
      return toast.error(
        "Location is required. Allow location access and retry.",
      );

    // Prefer sessionStorage (survives React state resets / page revisits via stored token)
    const signerEmail =
      sessionStorage.getItem(EMAIL_KEY(shipmentId)) ||
      gateEmail.trim().toLowerCase();
    const sessionToken = sessionStorage.getItem(SESSION_KEY(shipmentId));

    if (!signerEmail)
      return toast.error(
        "Could not determine your email. Please refresh and verify again.",
      );

    try {
      setSubmittingCustomerSign(true);
      const res = await pohAPI.customerDeliverySign(
        shipmentId,
        {
          signerName: customerSignerName,
          signerEmail,
          signatureData: customerSignatureData,
        },
        customerSignGeo.state.geo,
        customerSignFiles,
        sessionToken ?? undefined,
      );
      setDeliveryDriverSignUrl(res.data.data.deliveryDriverSignUrl ?? null);
      const nowIso = new Date().toISOString();
      setPohSignatureChain((prev) => {
        const filtered = prev.filter((s) => s.role !== "Recipient");
        return [
          ...filtered,
          {
            role: "Recipient",
            signerName: customerSignerName,
            signedAt: nowIso,
            signatureData: customerSignatureData,
          },
        ];
      });
      toast.success(
        shipment?.pohLevel === 2
          ? "Receipt signed! Delivery is now confirmed and complete."
          : "Receipt signed! Show the QR code to the driver so they can confirm delivery.",
      );
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(msg || "Failed to sign");
    } finally {
      setSubmittingCustomerSign(false);
    }
  };

  const STATUS_STYLE: Record<ReclamationStatus, string> = {
    open: "bg-blue-100 text-blue-700",
    under_review: "bg-amber-100 text-amber-700",
    approved: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
    settled: "bg-gray-100 text-gray-600",
  };

  const STATUS_LABEL: Record<ReclamationStatus, string> = {
    open: "Open",
    under_review: "Under Review",
    approved: "Approved",
    rejected: "Rejected",
    settled: "Settled",
  };

  // ── Loading ──
  if (phase === "loading") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader className="h-6 w-6 animate-spin text-blue-600" />
          <span className="text-gray-600">{t("publicView.loading")}</span>
        </div>
      </div>
    );
  }

  // ── Gate: email input ──
  if (phase === "email-input") {
    // Sub-phase: access requested successfully
    if (emailSubPhase === "requested") {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-8 space-y-6 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
              <Mail className="h-7 w-7 text-green-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">
                Request Sent
              </h1>
              <p className="text-sm text-gray-500">
                Your request has been submitted. You'll receive an email at{" "}
                <span className="font-medium text-gray-700">{gateEmail}</span>{" "}
                if the shipment owner approves your access.
              </p>
            </div>
            <button
              onClick={() => {
                setEmailSubPhase("idle");
                setGateEmail("");
              }}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Use a different email
            </button>
          </div>
        </div>
      );
    }

    // Sub-phase: email not in allowlist
    if (emailSubPhase === "denied") {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-8 space-y-5">
            <div className="text-center space-y-2">
              <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
                <Lock className="h-7 w-7 text-red-500" />
              </div>
              <h1 className="text-xl font-bold text-gray-900">Access Denied</h1>
              <p className="text-sm text-gray-500">
                <span className="font-medium text-gray-700">{gateEmail}</span>{" "}
                is not on the access list for this shipment.
              </p>
            </div>

            {alreadyRequested && (
              <div className="flex items-start space-x-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <Mail className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-800">
                  You've already requested access. Please wait for the shipment
                  owner to approve it.
                </p>
              </div>
            )}

            {canRequest && !alreadyRequested && (
              <button
                onClick={handleRequestAccess}
                disabled={requestingAccess}
                className="w-full inline-flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-60 font-medium"
              >
                {requestingAccess ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
                <span>
                  {requestingAccess ? "Sending request…" : "Request Access"}
                </span>
              </button>
            )}

            <button
              onClick={() => {
                setEmailSubPhase("idle");
                setGateEmail("");
                setCanRequest(false);
                setAlreadyRequested(false);
              }}
              className="w-full flex items-center justify-center space-x-1 text-sm text-gray-500 hover:text-gray-700 py-2"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              <span>Try a different email</span>
            </button>
          </div>
        </div>
      );
    }

    // Default: email entry
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-8 space-y-6">
          <div className="text-center space-y-2">
            <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
              <Lock className="h-7 w-7 text-blue-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">
              Restricted Shipment
            </h1>
            <p className="text-sm text-gray-500">
              Enter your email address. A verification code will be sent if you
              are on the access list.
            </p>
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Your email address
            </label>
            <div className="flex space-x-2">
              <input
                type="email"
                value={gateEmail}
                onChange={(e) => setGateEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
                placeholder="you@example.com"
                autoFocus
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm placeholder-gray-400"
              />
              <button
                onClick={handleSendOtp}
                disabled={sendingOtp}
                className="inline-flex items-center space-x-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60 text-sm font-medium"
              >
                {sendingOtp ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
                <span>{sendingOtp ? "Sending…" : "Send Code"}</span>
              </button>
            </div>
          </div>

          <div className="flex items-start space-x-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <Mail className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800">
              A 6-digit code will be emailed to you. The code is valid for 10
              minutes.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Gate: OTP input ──
  if (phase === "otp-input") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-8 space-y-6">
          <div className="text-center space-y-2">
            <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
              <Mail className="h-7 w-7 text-blue-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">
              Check your email
            </h1>
            <p className="text-sm text-gray-500">
              We sent a 6-digit code to{" "}
              <span className="font-medium text-gray-800">{gateEmail}</span>
            </p>
          </div>

          {/* 6-box OTP */}
          <div className="flex justify-center space-x-3">
            {otpValues.map((digit, i) => (
              <input
                key={i}
                ref={(el) => {
                  otpRefs.current[i] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpInput(i, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(i, e)}
                onPaste={i === 0 ? handleOtpPaste : undefined}
                className={`w-12 h-14 text-center text-xl font-bold border-2 rounded-lg focus:outline-none transition-colors ${
                  otpError
                    ? "border-red-400 bg-red-50 text-red-600"
                    : digit
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-gray-300 focus:border-blue-500"
                }`}
                disabled={verifyingOtp}
              />
            ))}
          </div>

          {verifyingOtp && (
            <div className="flex justify-center">
              <Loader className="h-5 w-5 animate-spin text-blue-600" />
            </div>
          )}

          {otpError && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 rounded-lg border border-red-200">
              <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
              <p className="text-sm text-red-700">{otpError}</p>
            </div>
          )}

          <div className="flex items-center justify-between text-sm">
            <button
              onClick={() => {
                setPhase("email-input");
                setOtpValues(["", "", "", "", "", ""]);
                setOtpError("");
              }}
              className="text-gray-500 hover:text-gray-700 flex items-center space-x-1"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              <span>Change email</span>
            </button>
            <button
              onClick={handleSendOtp}
              disabled={sendingOtp}
              className="text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
            >
              {sendingOtp ? "Sending…" : "Resend code"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Error / not found ──
  if (phase === "error" || !shipment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t("publicView.notFound")}
          </h2>
          <p className="text-gray-600">{t("publicView.notFoundDesc")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm mb-6 p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-dark-900">
              {t("publicView.shipment")} {shipment?.shipmentNumber}
            </h1>

            {/* ── Status badge with hover-popup stepper (from ShipmentReadonly) ── */}
            <div className="relative">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-full cursor-help transition-all hover:shadow-md ${getStatusColor(
                  shipment?.status || "",
                )}`}
                onMouseEnter={() => setIsStatusHovered(true)}
                onMouseLeave={() => setIsStatusHovered(false)}
              >
                {STATUS_STEPS.find((status) => status.key === shipment?.status)
                  ?.label || shipment?.status}
                <svg
                  className="w-3.5 h-3.5 opacity-60"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </span>

              {/* Status Stepper Hover Popup */}
              {isStatusHovered && (
                <div
                  className="absolute top-full mt-2 right-0 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4 min-w-[320px]"
                  onMouseEnter={() => setIsStatusHovered(true)}
                  onMouseLeave={() => setIsStatusHovered(false)}
                >
                  <div className="space-y-3">
                    {STATUS_STEPS.map((step, index) => {
                      const currentIndex = getCurrentStepIndex();
                      const isCompleted =
                        index < currentIndex ||
                        (shipment?.status === "Completed" &&
                          index === currentIndex);
                      const isCurrent =
                        index === currentIndex &&
                        shipment?.status !== "Completed";

                      return (
                        <div key={step.key} className="flex items-start gap-3">
                          <div
                            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mt-0.5 ${
                              isCompleted
                                ? "bg-green-500 text-white"
                                : isCurrent
                                  ? "bg-primary text-white"
                                  : "bg-gray-200 text-gray-500"
                            }`}
                          >
                            {isCompleted ? (
                              <svg
                                className="w-3 h-3"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <span>{index + 1}</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <div
                              className={`font-medium text-sm ${
                                isCompleted || isCurrent
                                  ? "text-dark-900"
                                  : "text-gray-500"
                              }`}
                            >
                              {step.label}
                            </div>
                            <div
                              className={`text-xs mt-0.5 ${
                                isCompleted
                                  ? "text-green-600"
                                  : isCurrent
                                    ? "text-primary"
                                    : "text-gray-400"
                              }`}
                            >
                              {getStepStatus(step.key)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-dark-700 mb-1">
                {t("publicView.referenceNumber")}
              </label>
              <p className="text-dark-900">
                {shipment?.referenceNumber || "-"}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-dark-700 mb-1">
                {t("publicView.date")}
              </label>
              <p className="text-dark-900">
                {new Date(shipment?.createdAt || "").toLocaleDateString(
                  "en-GB",
                )}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-dark-700 mb-1">
                {t("publicView.incoterm")}
              </label>
              <p className="text-dark-900">
                {shipment?.incoterms || "-"} {shipment?.place || ""}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-dark-700 mb-1">
                {t("publicView.loadType")}
              </label>
              <p className="text-dark-900">{loadTypeLabel || "-"}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-dark-700 mb-1">
                {t("readonly.transportMode")}
              </label>
              <p className="text-dark-900">
                {transportMode === "SEA"
                  ? t("transportMode.sea")
                  : transportMode === "AIR"
                    ? t("transportMode.air")
                    : t("transportMode.road")}
              </p>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pickup Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-2">
                <MapPin className="h-5 w-5 text-green-600" />
                <h2 className="text-lg font-semibold">
                  {t("publicView.pickup")}
                </h2>
              </div>
              <div className="flex items-center space-x-1 text-dark-900">
                <Clock className="h-4 w-4" />
                <span>
                  {shipment?.pickup?.date
                    ? new Date(shipment.pickup.date).toLocaleDateString("en-GB")
                    : "-"}{" "}
                  / {shipment?.pickup?.time || "-"}
                </span>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-dark-500">
                  {t("publicView.companyName")}
                </p>
                <p className="font-medium">
                  {shipment?.pickup?.companyName || "-"}
                  {shipment?.pickup?.registerationCode && (
                    <span className="text-gray-600 ml-1">
                      ({shipment?.pickup?.registerationCode})
                    </span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-dark-500">
                  {t("publicView.address")}
                </p>
                {shipment?.pickup?.address ? (
                  <p className="font-medium">
                    {shipment?.pickup?.address?.street || "-"},{" "}
                    {shipment?.pickup?.address?.city},{" "}
                    {countryOptions.find(
                      (option) =>
                        option.value === shipment?.pickup?.address?.country,
                    )?.label || "-"}
                    , {shipment?.pickup?.address?.postalCode}
                  </p>
                ) : (
                  "-"
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {shipment?.pickup?.emailAddress && (
                  <div>
                    <p className="text-sm text-dark-500">
                      {t("publicView.email")}
                    </p>
                    <p className="font-medium">
                      {shipment?.pickup?.emailAddress}
                    </p>
                  </div>
                )}
                {shipment?.pickup?.phoneNumber && (
                  <div>
                    <p className="text-sm text-dark-500">
                      {t("publicView.phone")}
                    </p>
                    <p className="font-medium">
                      {shipment?.pickup?.phoneNumber}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Delivery Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-2">
                <MapPin className="h-5 w-5 text-red-600" />
                <h2 className="text-lg font-semibold">
                  {t("publicView.delivery")}
                </h2>
              </div>
              <div className="flex items-center space-x-1 text-dark-900">
                <Clock className="h-4 w-4" />
                <span>
                  {shipment?.delivery?.date
                    ? new Date(shipment.delivery.date).toLocaleDateString(
                        "en-GB",
                      )
                    : "-"}{" "}
                  / {shipment?.delivery?.time || "-"}
                </span>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-dark-500">
                  {t("publicView.companyName")}
                </p>
                <p className="font-medium">
                  {shipment?.delivery?.companyName || "-"}
                  {shipment?.delivery?.registerationCode && (
                    <span className="text-gray-600 ml-1">
                      ({shipment?.delivery?.registerationCode})
                    </span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-dark-500">
                  {t("publicView.address")}
                </p>
                {shipment?.delivery?.address ? (
                  <p className="font-medium">
                    {shipment?.delivery?.address?.street || "-"},{" "}
                    {shipment?.delivery?.address?.city},{" "}
                    {countryOptions.find(
                      (option) =>
                        option.value === shipment?.delivery?.address?.country,
                    )?.label || "-"}
                    , {shipment?.delivery?.address?.postalCode}
                  </p>
                ) : (
                  "-"
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {shipment?.delivery?.emailAddress && (
                  <div>
                    <p className="text-sm text-dark-500">
                      {t("publicView.email")}
                    </p>
                    <p className="font-medium">
                      {shipment?.delivery?.emailAddress}
                    </p>
                  </div>
                )}
                {shipment?.delivery?.phoneNumber && (
                  <div>
                    <p className="text-sm text-dark-500">
                      {t("publicView.phone")}
                    </p>
                    <p className="font-medium">
                      {shipment?.delivery?.phoneNumber}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Carrier Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Truck className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold">
                {t("publicView.carrier")}
              </h2>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-dark-500">
                  {t("publicView.companyName")}
                </p>
                <p className="font-medium">
                  {shipment?.carrier?.companyName || "-"}
                  {shipment?.carrier?.registerationCode && (
                    <span className="text-gray-600 ml-1">
                      ({shipment?.carrier?.registerationCode})
                    </span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-dark-500">
                  {t("publicView.address")}
                </p>
                {shipment?.carrier?.address ? (
                  <p className="font-medium">
                    {shipment?.carrier?.address?.street || "-"},{" "}
                    {shipment?.carrier?.address?.city},{" "}
                    {countryOptions.find(
                      (option) =>
                        option.value === shipment?.carrier?.address?.country,
                    )?.label || "-"}
                    , {shipment?.carrier?.address?.postalCode}
                  </p>
                ) : (
                  "-"
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {shipment?.carrier?.emailAddress && (
                  <div>
                    <p className="text-sm text-dark-500">
                      {t("publicView.email")}
                    </p>
                    <p className="font-medium">
                      {shipment?.carrier?.emailAddress}
                    </p>
                  </div>
                )}
                {shipment?.carrier?.phoneNumber && (
                  <div>
                    <p className="text-sm text-dark-500">
                      {t("publicView.phone")}
                    </p>
                    <p className="font-medium">
                      {shipment?.carrier?.phoneNumber}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Client Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Building2 className="h-5 w-5 text-purple-600" />
              <h2 className="text-lg font-semibold">
                {t("publicView.client")}
              </h2>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-dark-500">
                  {t("publicView.companyName")}
                </p>
                <p className="font-medium">
                  {shipment?.client?.companyName || "-"}
                  {shipment?.client?.registerationCode && (
                    <span className="text-gray-600 ml-1">
                      ({shipment?.client?.registerationCode})
                    </span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-dark-500">
                  {t("publicView.address")}
                </p>
                {shipment?.client?.address ? (
                  <p className="font-medium">
                    {shipment?.client?.address?.street || "-"},{" "}
                    {shipment?.client?.address?.city},{" "}
                    {countryOptions.find(
                      (option) =>
                        option.value === shipment?.client?.address?.country,
                    )?.label || "-"}
                    , {shipment?.client?.address?.postalCode}
                  </p>
                ) : (
                  "-"
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {shipment?.client?.emailAddress && (
                  <div>
                    <p className="text-sm text-dark-500">
                      {t("publicView.email")}
                    </p>
                    <p className="font-medium">
                      {shipment?.client?.emailAddress}
                    </p>
                  </div>
                )}
                {shipment?.client?.phoneNumber && (
                  <div>
                    <p className="text-sm text-dark-500">
                      {t("publicView.phone")}
                    </p>
                    <p className="font-medium">
                      {shipment?.client?.phoneNumber}?
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Proof of Handover chain card ─── */}
        {phase === "granted" &&
          userRole === "poh_handler" &&
          shipment?.pohLevel !== 0 && (
            <div className="mt-6 space-y-4">
              {pohLoading ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex items-center gap-2 p-4 text-sm text-gray-400">
                  <Loader className="h-4 w-4 animate-spin" />
                  <span>Loading handover status…</span>
                </div>
              ) : (
                (() => {
                  const consignerSig = pohSignatureChain.find(
                    (s) => s.role === "Consignor",
                  );
                  const pickupDrvSig = pohSignatureChain.find(
                    (s) => s.role === "Pickup Driver",
                  );
                  const recipientSig = pohSignatureChain.find(
                    (s) => s.role === "Recipient",
                  );
                  const deliveryDrvSig = pohSignatureChain.find(
                    (s) => s.role === "Delivery Driver",
                  );

                  // Level 2: shipper + consignee only — no driver slots, and each
                  // phase is confirmed by that single signature.
                  const hideDriver = shipment?.pohLevel === 2;

                  const pickupStatus = (
                    hideDriver ? consignerSig : consignerSig && pickupDrvSig
                  )
                    ? "confirmed"
                    : "pending";

                  const deliveryDone = hideDriver
                    ? !!recipientSig
                    : !!(recipientSig && deliveryDrvSig);
                  const deliveryStarted = !!(recipientSig || deliveryDrvSig);
                  const deliveryStatus = deliveryDone
                    ? "confirmed"
                    : deliveryStarted
                      ? "pending"
                      : pickupStatus === "confirmed"
                        ? "pending"
                        : "upcoming";

                  const pickup: POHStepData = {
                    phase: "pickup",
                    status: pickupStatus,
                    slots: [
                      consignerSig
                        ? {
                            ...consignerSig,
                            role: "Consignor",
                            status: "confirmed" as const,
                          }
                        : { role: "Consignor", status: "pending" as const },
                      ...(hideDriver
                        ? []
                        : [
                            pickupDrvSig
                              ? {
                                  ...pickupDrvSig,
                                  role: "Pickup Driver",
                                  status: "confirmed" as const,
                                }
                              : {
                                  role: "Pickup Driver",
                                  status: consignerSig
                                    ? ("pending" as const)
                                    : ("upcoming" as const),
                                },
                          ]),
                    ],
                  };
                  const delivery: POHStepData = {
                    phase: "delivery",
                    status: deliveryStatus,
                    slots: [
                      recipientSig
                        ? {
                            ...recipientSig,
                            role: "Recipient",
                            status: "confirmed" as const,
                          }
                        : {
                            role: "Recipient",
                            status:
                              deliveryStatus === "upcoming"
                                ? "upcoming"
                                : ("pending" as const),
                          },
                      ...(hideDriver
                        ? []
                        : [
                            deliveryDrvSig
                              ? {
                                  ...deliveryDrvSig,
                                  role: "Delivery Driver",
                                  status: "confirmed" as const,
                                }
                              : {
                                  role: "Delivery Driver",
                                  status:
                                    deliveryStatus === "upcoming"
                                      ? ("upcoming" as const)
                                      : ("pending" as const),
                                },
                          ]),
                    ],
                  };

                  const overrideRecords = (((
                    shipment as unknown as {
                      pohStatusOverrides?: Array<{
                        reason: string;
                        byName?: string;
                        at?: string;
                      }>;
                    }
                  )?.pohStatusOverrides) ?? []);
                  const overrideNotes = overrideRecords.map((o) => ({
                    label: t(`statusChange.skipped.${o.reason}`),
                    meta: [
                      o.byName || null,
                      o.at
                        ? new Date(o.at).toLocaleString(undefined, {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · "),
                  }));

                  return (
                    <POHChainCard
                      pickup={pickup}
                      delivery={delivery}
                      overrideTitle={t("statusChange.overridesTitle")}
                      overrideNotes={overrideNotes}
                    />
                  );
                })()
              )}

              {/* Driver QR — after recipient signed, before driver signs */}
              {!pohLoading &&
                deliveryDriverSignUrl &&
                !pohSignatureChain.find(
                  (s) => s.role === "Delivery Driver",
                ) && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-3">
                    <p className="text-sm font-semibold text-gray-700">
                      Ask the driver to scan this QR code to confirm delivery
                    </p>
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <div className="inline-flex p-3 bg-white border border-gray-200 rounded-xl shadow-sm shrink-0">
                        <QRCodeSVG
                          value={deliveryDriverSignUrl}
                          size={130}
                          level="M"
                        />
                      </div>
                      <div className="space-y-1 text-center sm:text-left">
                        <p className="text-xs text-gray-500">
                          Or share the link with the driver:
                        </p>
                        <p className="text-xs text-blue-600 break-all">
                          {deliveryDriverSignUrl}
                        </p>
                        <p className="text-xs text-amber-600 font-medium mt-2">
                          Waiting for driver signature — shipment will be marked
                          Completed once both have signed.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              {/* Signing form — poh_handler only, before recipient has signed,
                  and only while the shipment is In Transit. Once it has been
                  completed (incl. a manual advance that bypassed this
                  signature), the form is hidden, not just blocked. */}
              {!pohLoading &&
                userRole === "poh_handler" &&
                shipment?.status === "InTransit" &&
                !pohSignatureChain.find((s) => s.role === "Recipient") && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-600" />
                      <p className="text-sm font-semibold text-gray-800">
                        Sign as Received
                      </p>
                    </div>
                    <form
                      onSubmit={handleCustomerDeliverySign}
                      className="p-4 space-y-4"
                    >
                      <p className="text-sm text-gray-600">
                        Please sign below to confirm you have received the goods
                        for this shipment.
                      </p>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Your Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={customerSignerName}
                          onChange={(e) =>
                            setCustomerSignerName(e.target.value)
                          }
                          placeholder="Full name of person receiving goods"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <SignaturePad
                        label="Your Signature"
                        onSave={setCustomerSignatureData}
                        width={600}
                        height={200}
                      />
                      {customerSignGeo.state.status === "idle" ? (
                        <button
                          type="button"
                          onClick={customerSignGeo.capture}
                          className="w-full py-2 text-sm rounded-lg border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100"
                        >
                          Share my location (required to sign)
                        </button>
                      ) : (
                        <GeoStatusBanner
                          state={customerSignGeo.state}
                          onRetry={customerSignGeo.capture}
                        />
                      )}
                      <FileDropzone
                        files={customerSignFiles}
                        onChange={setCustomerSignFiles}
                        disabled={submittingCustomerSign}
                        label="Proof photos (optional)"
                      />
                      <button
                        type="submit"
                        disabled={
                          submittingCustomerSign ||
                          customerSignGeo.state.status !== "ok"
                        }
                        className="w-full py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {submittingCustomerSign && (
                          <Loader className="h-4 w-4 animate-spin" />
                        )}
                        Sign as Received
                      </button>
                    </form>
                  </div>
                )}
            </div>
          )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Transport Information */}
          <div className="bg-white rounded-lg shadow-sm mt-6 p-6">
            <div className="flex items-center space-x-2 mb-4">
              {transportMode === "SEA" ? (
                <Ship className="h-5 w-5 text-blue-600" />
              ) : transportMode === "AIR" ? (
                <Plane className="h-5 w-5 text-indigo-600" />
              ) : (
                <Truck className="h-5 w-5 text-gray-600" />
              )}
              <h2 className="text-lg font-semibold">
                {t("publicView.transport")}
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {/* Driver + truck/trailer are ROAD-only */}
              {isRoad && (
                <>
                  <div>
                    <p className="text-sm text-dark-500">
                      {t("publicView.driver")}
                    </p>
                    <p className="font-medium">
                      {typeof shipment?.assignedDriver === "object"
                        ? shipment?.assignedDriver?.fullName
                        : shipment?.assignedDriver || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-dark-500">
                      {t("publicView.truck")}/{t("publicView.trailer")}
                    </p>
                    <p className="font-medium">
                      {typeof shipment?.carrier?.truckLicensePlate === "object"
                        ? shipment?.carrier?.truckLicensePlate
                            ?.registrationNumber
                        : shipment?.carrier?.truckLicensePlate || "-"}{" "}
                      /{" "}
                      {typeof shipment?.carrier?.trailerLicensePlate ===
                      "object"
                        ? shipment?.carrier?.trailerLicensePlate
                            ?.registrationNumber
                        : shipment?.carrier?.trailerLicensePlate || "-"}
                    </p>
                  </div>
                </>
              )}
              {/* Container: ROAD (optional) + SEA, hidden for AIR */}
              {transportMode !== "AIR" && (
                <div>
                  <p className="text-sm text-dark-500">
                    {t("publicView.container")}
                  </p>
                  <p className="font-medium">
                    {shipment?.carrier?.containerNumber || "-"}
                  </p>
                </div>
              )}
            </div>

            {/* ── SEA details ── */}
            {transportMode === "SEA" && shipment?.seaDetails && (
              <div className="border-t border-gray-100 mt-4 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Ship className="h-4 w-4 text-blue-600" />
                  <h3 className="text-sm font-semibold">{t("sea.title")}</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-dark-500">{t("sea.shippingLine")}</p>
                    <p className="font-medium">
                      {shipment.seaDetails.shippingLine || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-dark-500">{t("sea.vesselName")}</p>
                    <p className="font-medium">
                      {shipment.seaDetails.vesselName || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-dark-500">{t("sea.voyageNumber")}</p>
                    <p className="font-medium">
                      {shipment.seaDetails.voyageNumber || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-dark-500">{t("sea.portOfLoading")}</p>
                    <p className="font-medium">
                      {shipment.seaDetails.portOfLoading || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-dark-500">{t("sea.portOfDischarge")}</p>
                    <p className="font-medium">
                      {shipment.seaDetails.portOfDischarge || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-dark-500">{t("sea.masterBLNumber")}</p>
                    <p className="font-medium">
                      {shipment.seaDetails.masterBLNumber || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-dark-500">{t("sea.etd")}</p>
                    <p className="font-medium">
                      {formatDate(shipment.seaDetails.etd)}
                    </p>
                  </div>
                  <div>
                    <p className="text-dark-500">{t("sea.eta")}</p>
                    <p className="font-medium">
                      {formatDate(shipment.seaDetails.eta)}
                    </p>
                  </div>
                  {shipment.seaDetails.forwarder?.companyName && (
                    <>
                      <div>
                        <p className="text-dark-500">
                          {t("forwarder.companyName")}
                        </p>
                        <p className="font-medium">
                          {shipment.seaDetails.forwarder.companyName}
                        </p>
                      </div>
                      <div>
                        <p className="text-dark-500">
                          {t("forwarder.houseBLNumber")}
                        </p>
                        <p className="font-medium">
                          {shipment.seaDetails.forwarder.houseBLNumber || "-"}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ── AIR details ── */}
            {transportMode === "AIR" && shipment?.airDetails && (
              <div className="border-t border-gray-100 mt-4 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Plane className="h-4 w-4 text-indigo-600" />
                  <h3 className="text-sm font-semibold">{t("air.title")}</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-dark-500">{t("air.airlineName")}</p>
                    <p className="font-medium">
                      {shipment.airDetails.airlineName || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-dark-500">{t("air.flightNumber")}</p>
                    <p className="font-medium">
                      {shipment.airDetails.flightNumber || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-dark-500">
                      {t("air.airportOfDeparture")}
                    </p>
                    <p className="font-medium">
                      {shipment.airDetails.airportOfDeparture || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-dark-500">{t("air.airportOfArrival")}</p>
                    <p className="font-medium">
                      {shipment.airDetails.airportOfArrival || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-dark-500">{t("air.mawbNumber")}</p>
                    <p className="font-medium">
                      {shipment.airDetails.mawbNumber || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-dark-500">{t("air.uldNumber")}</p>
                    <p className="font-medium">
                      {shipment.airDetails.uldNumber || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-dark-500">{t("air.etd")}</p>
                    <p className="font-medium">
                      {formatDate(shipment.airDetails.etd)}
                    </p>
                  </div>
                  <div>
                    <p className="text-dark-500">{t("air.eta")}</p>
                    <p className="font-medium">
                      {formatDate(shipment.airDetails.eta)}
                    </p>
                  </div>
                  {shipment.airDetails.forwarder?.companyName && (
                    <>
                      <div>
                        <p className="text-dark-500">
                          {t("forwarder.companyName")}
                        </p>
                        <p className="font-medium">
                          {shipment.airDetails.forwarder.companyName}
                        </p>
                      </div>
                      <div>
                        <p className="text-dark-500">
                          {t("forwarder.hawbNumber")}
                        </p>
                        <p className="font-medium">
                          {shipment.airDetails.forwarder.hawbNumber || "-"}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Cargo Handling */}
          <div className="bg-white rounded-lg shadow-sm mt-6 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <h2 className="text-lg font-semibold">
                {t("publicView.cargoHandling")}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {shipment?.cargoHandling?.temperatureSensitive && (
                <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg">
                  <Thermometer className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-900">
                      {t("publicView.tempSensitive")}
                    </p>
                    <p className="text-sm text-blue-700">
                      {shipment?.cargoHandling?.temperatureRange ||
                        t("publicView.notSpecified")}
                    </p>
                  </div>
                </div>
              )}
              {shipment?.cargoHandling?.fragile && (
                <div className="flex items-center space-x-2 p-3 bg-orange-50 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-orange-600" />
                  <div>
                    <p className="font-medium text-orange-900">
                      {t("publicView.fragileCargo")}
                    </p>
                    <p className="text-sm text-orange-700">
                      {shipment?.cargoHandling?.specifications ||
                        t("publicView.specifications")}
                    </p>
                  </div>
                </div>
              )}
              {shipment?.cargoHandling?.adr && (
                <div className="flex items-center space-x-2 p-3 bg-red-50 rounded-lg">
                  <ShieldAlert className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="font-medium text-red-900">
                      {t("publicView.adr")}
                    </p>
                    <p className="text-sm text-red-700">
                      {t("publicView.imoClass")}:{" "}
                      {shipment?.cargoHandling?.imoClass || "N/A"}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cargo Items */}
        <div className="bg-white rounded-lg shadow-sm mt-6 p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Package className="h-5 w-5 text-purple-600" />
            <h2 className="text-lg font-semibold">
              {t("publicView.cargoInformation")}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left py-3 px-2 text-sm font-medium text-dark-600">
                    {t("publicView.qty")}
                  </th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-dark-600">
                    {t("publicView.cargoType")}
                  </th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-dark-600">
                    {t("publicView.weightKg")}
                  </th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-dark-600">
                    {t("publicView.volumeM3")}
                  </th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-dark-600">
                    {t("publicView.dimensions")}
                  </th>
                  {isRoad && (
                    <th className="text-left py-3 px-2 text-sm font-medium text-dark-600">
                      {t("publicView.ldm")}
                    </th>
                  )}
                  {isAir && (
                    <th className="text-left py-3 px-2 text-sm font-medium text-dark-600">
                      {t("publicView.chargeableWeight")}
                    </th>
                  )}
                  <th className="text-left py-3 px-2 text-sm font-medium text-dark-600">
                    {t("publicView.description")}
                  </th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-dark-600">
                    {t("publicView.marks")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {shipment?.cargoItems?.map((item, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-300 hover:bg-gray-50"
                  >
                    <td className="py-3 px-2">
                      {displayCargoNumber(item.quantity)}
                    </td>
                    <td className="py-3 px-2">{item.type || "-"}</td>
                    <td className="py-3 px-2">
                      {displayCargoNumber(item.weightKg)}
                    </td>
                    <td className="py-3 px-2">
                      {displayCargoNumber(item.volumeM3)}
                    </td>
                    <td className="py-3 px-2">
                      {displayCargoNumber(item.length)} x{" "}
                      {displayCargoNumber(item.width)} x{" "}
                      {displayCargoNumber(item.height)}
                    </td>
                    {isRoad && (
                      <td className="py-3 px-2">
                        {displayCargoNumber(item.ldm)}
                      </td>
                    )}
                    {isAir && (
                      <td className="py-3 px-2">
                        {displayCargoNumber(item.chargeableWeight)}
                      </td>
                    )}
                    <td className="py-3 px-2">{item.description || "N/A"}</td>
                    <td className="py-3 px-2">{item.marks || "N/A"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {shipment?.cargoItems && shipment?.cargoItems.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-dark-700">
                  {t("publicView.total")}:
                </span>
                <div className="flex gap-8">
                  <div className="text-sm">
                    <span className="font-medium text-dark-500">
                      {t("publicView.totalQuantity")}:
                    </span>{" "}
                    <span className="font-semibold text-dark-900">
                      {shipment?.totalQuantity || 0}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="font-medium text-dark-500">
                      {t("publicView.totalWeight")}:
                    </span>{" "}
                    <span className="font-semibold text-dark-900">
                      {shipment?.totalWeight?.toFixed(2) || 0} kg
                    </span>
                  </div>
                  {isRoad && (
                    <div className="text-sm">
                      <span className="font-medium text-dark-500">
                        {t("publicView.totalLdm")}:
                      </span>{" "}
                      <span className="font-semibold text-dark-900">
                        {totalLDM.toFixed(3)}
                      </span>
                    </div>
                  )}
                  <div className="text-sm">
                    <span className="font-medium text-dark-500">
                      {t("publicView.totalVolume")}:
                    </span>{" "}
                    <span className="font-semibold text-dark-900">
                      {totalVolume.toFixed(3)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {(!shipment?.cargoItems || shipment.cargoItems.length === 0) && (
            <div className="text-center py-8">
              <p className="text-dark-500">{t("publicView.noCargo")}</p>
            </div>
          )}
        </div>

        {/* ── Documents & Upload section ── */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-6">
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <h3 className="text-lg font-semibold text-dark-900 flex items-center gap-2">
                <File className="h-5 w-5 text-gray-600" />
                <span>{t("readonly.files")}</span>
              </h3>
              <div className="relative w-fit">
                <input
                  type="file"
                  id="public-file-upload"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.xls,.xlsx,.zip,.rar"
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length) {
                      await handlePublicUpload(files);
                      e.target.value = "";
                    }
                  }}
                  disabled={uploading}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <button
                  type="button"
                  disabled={uploading}
                  className="inline-flex items-center gap-1 px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50 text-sm"
                >
                  {uploading ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  <span>{t("publicView.uploadFiles")}</span>
                </button>
              </div>
            </div>
          </div>

          <div className="p-4 space-y-6">
            {/* Client uploaded files */}
            {shipment?.publicAttachments &&
              shipment.publicAttachments.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-dark-700 mb-2">
                    {t("publicView.clientLabel")}
                  </h4>
                  <div className="space-y-2">
                    {shipment.publicAttachments.map((file) => (
                      <div
                        key={file._id}
                        className="flex sm:items-center sm:justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 gap-3"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="shrink-0">
                            <File className="h-5 w-5 text-gray-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-dark-900 truncate">
                              {file.originalName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(file.size)} •{" "}
                              {new Date(file.uploadedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => window.open(file.fileUrl, "_blank")}
                            className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                            title={t("readonly.openFile")}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* POH Confirmation files */}
            {shipment?.driverAttachments &&
              shipment.driverAttachments.filter(
                (file) => file.attachmentType === "poh",
              ).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-dark-700 mb-2">
                    {t("readonly.pohLabel")}
                  </h4>
                  <div className="space-y-2">
                    {shipment.driverAttachments
                      .filter((file) => file.attachmentType === "poh")
                      .map((file) => (
                        <div
                          key={file._id}
                          className="flex sm:items-center sm:justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 gap-3"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="shrink-0">
                              <File className="h-5 w-5 text-gray-500" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-dark-900 truncate">
                                {file.originalName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(file.size)} •{" "}
                                {new Date(file.uploadedAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => openAttachment(file)}
                              className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                              title={t("readonly.openFile")}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => downloadAttachment(file)}
                              className="p-1 text-gray-500 hover:text-green-600 transition-colors"
                              title={t("readonly.downloadFile")}
                            >
                              <Download className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

            {/* Driver files */}
            {shipment?.driverAttachments &&
              shipment.driverAttachments.filter(
                (file) => file.attachmentType !== "poh",
              ).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-dark-700 mb-2">
                    {t("readonly.driverLabel")}
                  </h4>
                  <div className="space-y-2">
                    {shipment.driverAttachments
                      .filter((file) => file.attachmentType !== "poh")
                      .map((file) => (
                        <div
                          key={file._id}
                          className="flex sm:items-center sm:justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 gap-3"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="shrink-0">
                              <File className="h-5 w-5 text-gray-500" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-dark-900 truncate">
                                {file.originalName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(file.size)} •{" "}
                                {new Date(file.uploadedAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => openAttachment(file)}
                              className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                              title={t("readonly.openFile")}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => downloadAttachment(file)}
                              className="p-1 text-gray-500 hover:text-green-600 transition-colors"
                              title={t("readonly.downloadFile")}
                            >
                              <Download className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

            {/* Admin files */}
            {shipment?.attachments && shipment.attachments.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-dark-700 mb-2">
                  {t("readonly.adminLabel")}
                </h4>
                <div className="space-y-2">
                  {shipment.attachments.map((file) => (
                    <div
                      key={file._id}
                      className="flex sm:items-center sm:justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 gap-3"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="shrink-0">
                          <File className="h-5 w-5 text-gray-500" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-dark-900 truncate">
                            {file.originalName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(file.size)} •{" "}
                            {new Date(file.uploadedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => openAttachment(file)}
                          className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                          title={t("readonly.openFile")}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() =>
                            handleDownloadShipmentAttachment(file._id, false)
                          }
                          className="p-1 text-gray-500 hover:text-green-600 transition-colors"
                          title={t("readonly.downloadFile")}
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {(!shipment?.publicAttachments ||
              shipment.publicAttachments.length === 0) &&
              (!shipment?.driverAttachments ||
                shipment.driverAttachments.length === 0) &&
              (!shipment?.attachments || shipment.attachments.length === 0) && (
                <div className="text-center py-8">
                  <File className="h-12 w-12 text-dark-400 mx-auto" />
                  <h3 className="mt-2 text-sm font-medium text-dark-900">
                    {t("attachments.noAttachments")}
                  </h3>
                  <p className="mt-1 text-sm text-dark-500">
                    {t("readonly.noDriverAttachments")}
                  </p>
                </div>
              )}
          </div>
        </div>

        {/* Additional Notes */}
        <div className="bg-white rounded-lg shadow-sm mt-6 p-6">
          <h2 className="text-lg font-semibold mb-2">
            {t("publicView.remarksTitle")}
          </h2>
          <p className="text-dark-900 whitespace-pre-wrap">
            {shipment?.additionalNotes || "-"}
          </p>
        </div>

        {/* Reclamation section */}
        <div className="mt-6 space-y-3">
          {/* Existing reclamations */}
          {loadingReclamations && (
            <div className="flex items-center space-x-2 text-sm text-gray-400 py-2">
              <Loader className="h-4 w-4 animate-spin" />
              <span>Loading your reclamations…</span>
            </div>
          )}

          {!loadingReclamations && myReclamations.length > 0 && (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <p className="text-sm font-semibold text-gray-800">
                  Your Reclamations
                </p>
              </div>
              <div className="divide-y divide-gray-100">
                {myReclamations.map((rec) => (
                  <div
                    key={rec._id}
                    className="flex items-center justify-between px-4 py-3"
                  >
                    <div>
                      <p className="text-sm font-semibold font-mono text-gray-800">
                        {rec.reclamationNumber}
                      </p>
                      <p className="text-xs text-gray-400 capitalize mt-0.5">
                        {rec.type.replace(/_/g, " ")} ·{" "}
                        {new Date(rec.createdAt).toLocaleDateString(undefined, {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </p>
                      {rec.resolutionNote && (
                        <p className="text-xs text-gray-500 mt-1 italic">
                          "{rec.resolutionNote}"
                        </p>
                      )}
                    </div>
                    <span
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[rec.status]}`}
                    >
                      {STATUS_LABEL[rec.status]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* File (another) reclamation */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="font-semibold text-amber-900 text-sm">
                {myReclamations.length > 0
                  ? "File another reclamation"
                  : "Issue with this shipment?"}
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                {myReclamations.length > 0
                  ? "Submit a new claim for a different issue with this shipment."
                  : "If cargo was damaged, lost, or delivered incorrectly you can file a formal reclamation."}
              </p>
            </div>
            <button
              onClick={() => setReclamationOpen(true)}
              className="shrink-0 inline-flex items-center px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors"
            >
              {myReclamations.length > 0
                ? "File Another"
                : "File a Reclamation"}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-dark-500">
          <p>
            {t("publicView.shipmentDetails")} {shipment?.shipmentNumber}
          </p>
        </div>
      </div>

      {/* POH section moved into main layout flow above (after pickup/delivery section) */}

      {/* Reclamation modal */}
      <ReclamationModal
        isOpen={reclamationOpen}
        onClose={() => setReclamationOpen(false)}
        onFiled={() => {
          publicReclamationAPI
            .getReclamations(shipmentId, "customer")
            .then((res) => setMyReclamations(res.data.data.reclamations ?? []))
            .catch(() => {
              /* silent */
            });
        }}
        shipmentId={shipmentId}
        shipmentNumber={shipment?.shipmentNumber ?? ""}
        prefillEmail={resolvedEmail}
      />
    </div>
  );
};

export default ShipmentPublicView;
